//src/pages/gro/WorkerGroupFormPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw, Save, Users } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PermissionButton } from "@/components/shared/PermissionButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useCreateWorkerGroup,
  useRefreshWorkerGroup,
  useSetWorkerGroupStatus,
  useUpdateWorkerGroup,
  useWorkerGroupDetail,
} from "@/hooks/useWorkerGroups";
import { useUnits } from "@/hooks/useUnits";
import { useDepartments } from "@/hooks/useDepartments";
import { useJobPositions } from "@/hooks/useJobPositions";
import { useUsers } from "@/hooks/useUsers";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { WorkerGroupUpsertIn } from "@/types/gro";

const LIMIT_USERS = 50;

const emptyForm: WorkerGroupUpsertIn = {
  name: "",
  description: "",
  group_type: "manual",
  filters: {
    unit_ids: [],
    department_ids: [],
    job_position_ids: [],
  },
  user_ids: [],
};

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export default function WorkerGroupFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");

  const canWrite =
    user?.permissions?.includes(PERMISSIONS.WORKER_GROUPS_WRITE) ?? false;

  const detailQ = useWorkerGroupDetail(id);
  const createMut = useCreateWorkerGroup();
  const updateMut = useUpdateWorkerGroup();
  const setStatusMut = useSetWorkerGroupStatus();
  const refreshMut = useRefreshWorkerGroup();

  const unitsQ = useUnits();
  const departmentsQ = useDepartments();
  const jobPositionsQ = useJobPositions();
  const usersQ = useUsers({ q: "", limit: LIMIT_USERS, offset: 0 });

  const units = unitsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const jobPositions = jobPositionsQ.data ?? [];
  const usersList =
    (usersQ.data ?? []).map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      unit_name: u.unit_name,
    })) || [];

  const [form, setForm] = useState<WorkerGroupUpsertIn>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!detailQ.data) return;

    setForm({
      name: detailQ.data.name,
      description: detailQ.data.description || "",
      group_type: detailQ.data.group_type,
      filters: detailQ.data.filters ?? {
        unit_ids: [],
        department_ids: [],
        job_position_ids: [],
      },
      user_ids: detailQ.data.members.map((m) => m.user_id),
    });
  }, [detailQ.data]);

  const filters = form.filters ?? {
    unit_ids: [],
    department_ids: [],
    job_position_ids: [],
  };

  const selectedUsers = useMemo(
    () => usersList.filter((u) => form.user_ids.includes(u.id)),
    [form.user_ids, usersList],
  );

  function goBack() {
    navigate("/gro/worker-groups");
  }

  async function handleSave() {
    if (isViewing) return;

    if (!form.name.trim()) {
      toast.error("Informe o nome do grupo.");
      return;
    }

    if (form.group_type === "manual" && form.user_ids.length === 0) {
      toast.error("Selecione ao menos um usuário para o grupo manual.");
      return;
    }

    if (
      form.group_type === "dynamic" &&
      !filters.unit_ids.length &&
      !filters.department_ids.length &&
      !filters.job_position_ids.length
    ) {
      toast.error("Informe ao menos um filtro para o grupo dinâmico.");
      return;
    }

    setSaving(true);
    try {
      if (isEditing && id) {
        await updateMut.mutateAsync({ id, body: form });
        toast.success("Grupo atualizado com sucesso.");
        navigate(`/gro/worker-groups/${id}`);
      } else {
        const created = await createMut.mutateAsync(form);
        toast.success("Grupo criado com sucesso.");
        navigate(`/gro/worker-groups/${created.id}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar grupo.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus() {
    if (!detailQ.data) return;

    try {
      await setStatusMut.mutateAsync({
        id: detailQ.data.id,
        is_active: !detailQ.data.is_active,
      });

      toast.success(
        detailQ.data.is_active ? "Grupo inativado com sucesso." : "Grupo ativado com sucesso.",
      );
      setConfirmOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status do grupo.");
    }
  }

  async function handleRefresh() {
    if (!id) return;

    try {
      await refreshMut.mutateAsync(id);
      toast.success("Grupo recalculado com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao recalcular grupo.");
    }
  }

  if ((isViewing || isEditing) && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isViewing
                  ? "Visualizar Grupo de Avaliação"
                  : isEditing
                    ? "Editar Grupo de Avaliação"
                    : "Novo Grupo de Avaliação"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isViewing
                  ? "Consulte os dados, filtros e participantes do grupo."
                  : isEditing
                    ? "Atualize as regras e participantes do grupo."
                    : "Preencha os dados para criar um grupo manual ou dinâmico."}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              {isViewing ? "Voltar" : "Cancelar"}
            </Button>

            {isViewing ? (
              <>
                {detailQ.data ? (
                  <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={refreshMut.isPending}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Recalcular
                  </Button>
                ) : null}

                {detailQ.data ? (
                  <Button
                    variant="outline"
                    onClick={() => setConfirmOpen(true)}
                    disabled={setStatusMut.isPending}
                  >
                    {detailQ.data.is_active ? "Inativar" : "Ativar"}
                  </Button>
                ) : null}

                <PermissionButton
                  permission={PERMISSIONS.WORKER_GROUPS_WRITE}
                  onClick={() => navigate(`/gro/worker-groups/${id}/editar`)}
                >
                  Editar
                </PermissionButton>
              </>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      {detailQ.isError ? (
        <Card className="mb-6 p-6">
          <div className="text-sm text-destructive">
            {(detailQ.error as any)?.message || "Erro ao carregar grupo"}
          </div>
        </Card>
      ) : null}

      {isViewing && detailQ.data ? (
        <Card className="mb-6">
          <CardContent className="flex flex-wrap gap-2 pt-6">
            <Badge variant={detailQ.data.is_active ? "default" : "secondary"}>
              {detailQ.data.is_active ? "Ativo" : "Inativo"}
            </Badge>
            <Badge variant={detailQ.data.group_type === "dynamic" ? "default" : "secondary"}>
              {detailQ.data.group_type === "dynamic" ? "Dinâmico" : "Manual"}
            </Badge>
            <Badge variant="outline">{detailQ.data.member_count} participante(s)</Badge>
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados do grupo</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Nome *</Label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={isViewing}
                placeholder="Ex.: Operadores da Linha 3"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <Input
                className="mt-1"
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                disabled={isViewing}
                placeholder="Descrição opcional"
              />
            </div>

            <div className="max-w-xs">
              <Label>Tipo do grupo</Label>
              <Select
                value={form.group_type}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    group_type: value as "manual" | "dynamic",
                  }))
                }
                disabled={isViewing}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="dynamic">Dinâmico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {form.group_type === "manual" ? (
          <Card>
            <CardHeader>
              <CardTitle>Participantes do grupo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[420px] space-y-2 overflow-auto rounded-lg border p-3">
                {usersList.map((item) => {
                  const checked = form.user_ids.includes(item.id);

                  return (
                    <label
                      key={item.id}
                      className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isViewing}
                        onCheckedChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            user_ids: toggleId(prev.user_ids, item.id),
                          }))
                        }
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.email || "-"}
                          {item.unit_name ? ` • ${item.unit_name}` : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {selectedUsers.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedUsers.map((item) => (
                    <Badge key={item.id} variant="secondary">
                      {item.name}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Filtros do grupo dinâmico</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div>
                <Label>Unidades</Label>
                <div className="mt-2 max-h-[280px] space-y-2 overflow-auto rounded-lg border p-3">
                  {units.map((item) => (
                    <label key={item.id} className="flex cursor-pointer items-start gap-3 py-1.5">
                      <Checkbox
                        checked={filters.unit_ids.includes(item.id)}
                        disabled={isViewing}
                        onCheckedChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            filters: {
                              ...filters,
                              unit_ids: toggleId(filters.unit_ids, item.id),
                            },
                          }))
                        }
                      />
                      <div className="text-sm">{item.name}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Departamentos</Label>
                <div className="mt-2 max-h-[280px] space-y-2 overflow-auto rounded-lg border p-3">
                  {departments.map((item) => (
                    <label key={item.id} className="flex cursor-pointer items-start gap-3 py-1.5">
                      <Checkbox
                        checked={filters.department_ids.includes(item.id)}
                        disabled={isViewing}
                        onCheckedChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            filters: {
                              ...filters,
                              department_ids: toggleId(filters.department_ids, item.id),
                            },
                          }))
                        }
                      />
                      <div className="text-sm">{item.name}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Cargos</Label>
                <div className="mt-2 max-h-[280px] space-y-2 overflow-auto rounded-lg border p-3">
                  {jobPositions.map((item) => (
                    <label key={item.id} className="flex cursor-pointer items-start gap-3 py-1.5">
                      <Checkbox
                        checked={filters.job_position_ids.includes(item.id)}
                        disabled={isViewing}
                        onCheckedChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            filters: {
                              ...filters,
                              job_position_ids: toggleId(filters.job_position_ids, item.id),
                            },
                          }))
                        }
                      />
                      <div className="text-sm">{item.name}</div>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isViewing && detailQ.data?.members?.length ? (
          <Card>
            <CardHeader>
              <CardTitle>Membros atuais</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {detailQ.data.members.map((member) => (
                  <div key={member.user_id} className="rounded-lg border p-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      <div className="font-medium">{member.name || member.user_id}</div>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {member.email || "-"}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={detailQ.data?.is_active ? "Inativar grupo" : "Ativar grupo"}
        description={
          detailQ.data
            ? `Deseja ${detailQ.data.is_active ? "inativar" : "ativar"} o grupo "${detailQ.data.name}"?`
            : ""
        }
        onConfirm={handleToggleStatus}
        confirmLabel={detailQ.data?.is_active ? "Inativar" : "Ativar"}
      />
    </div>
  );
}
